import * as React from "react";
import { User, Copy, Check, Mail, Phone, Coins, Briefcase } from "lucide-react";
import copy from "copy-to-clipboard";

import * as DTO from "@/interfaces";
import { editorQueueListUrl, extractInitials, fullNameFrom, useSocialCoachSession } from "@/utils";
import { Avatar, Badge, Button, Dialog, Skeleton } from "@/components/ui";
import { CoachesServices, PlayersServices } from "@/services/bedrock";

import { Stickers } from "../Stickers";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PlayersServices as LegacyPlayerServices } from "@/services";

interface Props {
  videoProjectId: number;
  playerId: number | string;
  coachId?: string | number;

  industryRole: string | null;
}

export const EditorPlayerInfo: React.FC<Props> = ({ playerId, industryRole, videoProjectId, coachId }) => {
  const history = useHistory();
  const { isAdmin } = useSocialCoachSession();
  const [isOpen, setIsOpen] = React.useState(false);
  const [copiedField, setCopiedField] = React.useState<"email" | "phone" | "company" | null>(null);

  const { data: playerInfo, isLoading: isPlayerLoading } = useQuery({
    queryKey: ["player", playerId],
    queryFn: () =>
      PlayersServices.getPlayer(playerId, ["region_names", "branch_names", "video_project_credits_status"]),
    enabled: !!playerId,
  });
  const videoProjectCreditsStatus = playerInfo?.videoProjectCreditsStatus;

  const { data: stickers, isLoading: isStickersLoading } = useQuery({
    queryKey: ["stickers", playerId, coachId],
    queryFn: () => LegacyPlayerServices.getAllPlayerStickers(playerId, coachId!),
    enabled: !!playerId && !!coachId,
  });

  const { data: coachPublicInfo, isLoading: isCoachLoading } = useQuery({
    queryKey: ["publicCoach", coachId],
    queryFn: () => CoachesServices.getPublicCoach(Number(coachId)),
    enabled: !!coachId,
  });

  const coachName = coachPublicInfo?.companyName || null;

  const regionsAndBranches = React.useMemo(() => {
    return [...(playerInfo?.regionNames || []), ...(playerInfo?.branchNames || [])].filter(Boolean);
  }, [playerInfo?.regionNames, playerInfo?.branchNames]);

  const mediaList = React.useMemo(() => {
    const list: DTO.Media[] = [];
    stickers?.playerSticker?.stickers?.media?.forEach(m => list.push(m));
    stickers?.coachSticker?.stickers?.media?.forEach(m => list.push(m));
    return list;
  }, [stickers?.playerSticker?.stickers?.media, stickers?.coachSticker?.stickers?.media]);

  const handleCopy = (text: string, field: "email" | "phone" | "company") => {
    copy(text);
    setCopiedField(field);
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  const creditsUsedLabel = videoProjectCreditsStatus
    ? `${videoProjectCreditsStatus.creditsUsed || 0}/${
        videoProjectCreditsStatus.unlimitedCredits ? "∞" : videoProjectCreditsStatus.creditsAllotted
      } credits used`
    : null;

  const goToAllProjects = () => {
    let url = isAdmin ? editorQueueListUrl() : editorQueueListUrl("grid");
    const params = new URLSearchParams();
    if (playerInfo?.email) {
      params.set("search", playerInfo.email);
    } else if (playerInfo?.firstName) {
      params.set("search", [playerInfo.firstName, playerInfo.lastName].filter(Boolean).join(" "));
    }
    url = [url, params.toString()].filter(Boolean).join("?");
    history.push(url);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Title block */}
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <User className="size-4.5 text-emerald-600" />
          Client Info
        </label>
      </div>

      {/* Main card container */}
      <div className="videoProjectContainer bg-slate-50/50 p-5 rounded-xl border border-slate-200">
        {/* Main Content Row / Columns */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
          {/* Column 1: Avatar, Primary Details & Actions */}
          <div className="md:col-span-2 flex flex-col gap-4 min-w-0">
            {/* Profile Info Row */}
            <div className="flex flex-row items-center gap-4 min-w-0">
              {isPlayerLoading ? (
                <>
                  <Skeleton className="size-16 rounded-full shrink-0" />
                  <div className="flex flex-col gap-1.5 flex-1 text-left">
                    <Skeleton className="h-5 w-36" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3.5 w-28 mt-0.5" />
                  </div>
                </>
              ) : (
                <>
                  <Avatar className="size-16 border border-slate-200 shadow-sm shrink-0">
                    <Avatar.Image src={playerInfo?.photoUrl || undefined} />
                    <Avatar.Fallback className="text-lg font-bold text-slate-500 bg-slate-100">
                      {playerInfo ? extractInitials(playerInfo.firstName, playerInfo.lastName) : ""}
                    </Avatar.Fallback>
                  </Avatar>
                  <div className="flex flex-col text-sm text-slate-500 gap-0.5 min-w-0 text-left">
                    <div className="font-bold text-base text-slate-800 leading-snug">
                      {playerInfo ? fullNameFrom(playerInfo) : ""}
                    </div>
                    {industryRole && (
                      <div className="font-medium text-slate-600 truncate leading-tight">{industryRole}</div>
                    )}
                    {creditsUsedLabel && (
                      <div className="text-xs text-slate-400 font-semibold truncate leading-tight uppercase tracking-wider mt-0.5 flex items-center gap-1 select-none">
                        <Coins className="size-3.5 text-slate-400 shrink-0" />
                        <span>{creditsUsedLabel}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Actions Row */}
            <div className="flex flex-row flex-wrap items-center gap-2.5 select-none pt-0.5">
              {isPlayerLoading ? (
                <>
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-8 w-28" />
                </>
              ) : (
                <>
                  {isStickersLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : mediaList.length > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50/50 hover:text-indigo-700 font-semibold"
                      onClick={() => setIsOpen(true)}
                    >
                      <span>View Stickers ({mediaList.length})</span>
                    </Button>
                  ) : null}
                  {(playerInfo?.email || playerInfo?.firstName) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-100 font-semibold"
                      onClick={goToAllProjects}
                    >
                      View all projects
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Column 2: Contact Details */}
          <div className="md:col-span-3 flex flex-col gap-2 text-xs text-slate-600 border-t md:border-t-0 md:border-l border-slate-200/60 pt-4 md:pt-0 md:pl-6">
            {/* Coach / Company Info */}
            {isCoachLoading ? (
              <div className="flex flex-row items-center gap-2 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200/40 flex-wrap">
                <Briefcase className="size-4 text-slate-300 shrink-0" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
              </div>
            ) : coachName ? (
              <div className="flex flex-row items-start justify-between gap-3 bg-white hover:bg-slate-100/50 px-2.5 py-1.5 rounded-lg border border-slate-200/40 transition-colors">
                <div className="flex flex-row items-center gap-2 flex-wrap min-w-0 flex-1">
                  <Briefcase className="size-4 text-slate-400 shrink-0 mt-0.5" />
                  <span className="font-medium text-slate-700">{coachName}</span>
                  {regionsAndBranches.length > 0 && (
                    <div className="flex flex-row gap-1 flex-wrap items-center">
                      {regionsAndBranches.map((segment, index) => (
                        <Badge
                          key={`segment_${index}`}
                          variant="outline"
                          className="text-[10px] text-slate-600 border-slate-100 font-medium"
                        >
                          {segment}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(coachName, "company")}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all cursor-pointer border-none outline-none shrink-0"
                  title="Copy company name to clipboard"
                >
                  {copiedField === "company" ? (
                    <Check className="size-3.5 text-emerald-600 animate-bounce" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </button>
              </div>
            ) : null}

            {/* Email Field */}
            {isPlayerLoading ? (
              <div className="flex items-center justify-between bg-white px-2.5 py-1.5 rounded-lg border border-slate-200/40">
                <div className="flex items-center gap-2">
                  <Mail className="size-4 text-slate-300 shrink-0" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            ) : playerInfo?.email ? (
              <div className="flex items-center justify-between group/row bg-white hover:bg-slate-100/50 px-2.5 py-1.5 rounded-lg border border-slate-200/40 transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  <Mail className="size-4 text-slate-400 shrink-0" />
                  <span className="truncate font-medium text-slate-700">{playerInfo.email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(playerInfo.email!, "email")}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all cursor-pointer border-none outline-none"
                  title="Copy email to clipboard"
                >
                  {copiedField === "email" ? (
                    <Check className="size-3.5 text-emerald-600 animate-bounce" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </button>
              </div>
            ) : null}

            {/* Phone Field */}
            {isPlayerLoading ? (
              <div className="flex items-center justify-between bg-white px-2.5 py-1.5 rounded-lg border border-slate-200/40">
                <div className="flex items-center gap-2">
                  <Phone className="size-4 text-slate-300 shrink-0" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            ) : playerInfo?.phone ? (
              <div className="flex items-center justify-between group/row bg-white hover:bg-slate-100/50 px-2.5 py-1.5 rounded-lg border border-slate-200/40 transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="size-4 text-slate-300 shrink-0" />
                  <span className="truncate font-medium text-slate-700">{playerInfo.phone}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(playerInfo.phone!, "phone")}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all cursor-pointer border-none outline-none"
                  title="Copy phone to clipboard"
                >
                  {copiedField === "phone" ? (
                    <Check className="size-3.5 text-emerald-600 animate-bounce" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen} size="xl">
        <Dialog.Header>
          <Dialog.Title>Player Stickers</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-5 overflow-y-auto max-h-[80vh]">
          <Stickers videoProjectId={videoProjectId} stickers={stickers} />
        </Dialog.Content>
        <Dialog.Footer showCloseButton />
      </Dialog>
    </div>
  );
};
